import { SetStateAction } from "react";

import { Trans } from "react-i18next";

import { TFunction } from "i18next";

import TextLink, {
    TEXT_LINK_SIZES
} from "components/DesignComponents/TextLink";
import { getFirstLetterUppercase, sortAndReturnArray } from "shared/functions";
import { PLATFORMS, TASK_TYPES } from "shared/types/enums";

import InfoBanner from "../InfoBanner";
import { CanAdapterType } from "../SecondSelectContent/CanAdapters/types";
import {
    SortedVehicles,
    Vehicle,
    Vehicles
} from "../SecondSelectContent/CanVehicles/types";
import { AdditionalData } from "../SecondSelectContent/GeneralDropdown/types";
import { UNSUPPORTED_FIRMWARE_LIST } from "../SecondSelectContent/GeneralDropdown/unsupportedFirmwareList";

const renderCanAdapterType = (adapter: CanAdapterType) =>
    `${adapter.type} (${adapter.seo_name})`;

const getParsedAttributes = (attributes: string | null) => {
    if (attributes) {
        try {
            const parsedData = JSON.parse(attributes);
            const firmwareVersion: string | null = parsedData?.version || null;
            const specId: number | null = parsedData?.spec_id || null;
            const platformId: number | null = parsedData?.platform_id || null;

            return { firmwareVersion, specId, platformId };
        } catch {
            return null;
        }
    }

    return null;
};

const isUnsupportedFirmware = (attributes: string | null) => {
    const parsedData = getParsedAttributes(attributes);

    if (parsedData?.firmwareVersion && parsedData?.platformId === 1) {
        const { firmwareVersion, specId } = parsedData;

        return UNSUPPORTED_FIRMWARE_LIST.some(
            item => item.fwVersion === firmwareVersion && item.specId === specId
        );
    }

    return false;
};

const hasDowngradeProtection = (attributes: string | null) => {
    const parsedData = getParsedAttributes(attributes);

    if (parsedData?.firmwareVersion) {
        const { firmwareVersion } = parsedData;

        const majorVersion = Number(firmwareVersion.split(".")[0]);
        return majorVersion >= 4 && majorVersion < 9;
    }

    return false;
};

const renderAdditionalBanner = (
    taskType: TASK_TYPES,
    data: AdditionalData,
    t: TFunction<"translation", undefined>
) => {
    const isUploadFwBundleContainer = taskType === TASK_TYPES.TxFirmwareBundle;
    const isUploadTlsCertificate = taskType === TASK_TYPES.TxUserCertificate;

    if (isUploadFwBundleContainer) {
        return (
            <InfoBanner
                title={
                    <Trans
                        i18nKey="Dialog##upload firmware bundle container info"
                        components={{
                            strong: <strong />,
                            a: (
                                <TextLink
                                    size={TEXT_LINK_SIZES.Small}
                                    target="_blank"
                                    href="https://wiki.teltonika-gps.com/view/Device_Firmware_Update"
                                >
                                    ""
                                </TextLink>
                            )
                        }}
                    />
                }
            />
        );
    }

    if (isUploadTlsCertificate) {
        return (
            <InfoBanner
                title={
                    <Trans
                        i18nKey="Dialog##tls certificate transfer info"
                        components={{
                            strong: <strong />
                        }}
                    />
                }
            />
        );
    }

    if (isUnsupportedFirmware(data.fileAttributes)) {
        return (
            <InfoBanner title={t("Dialog##upload firmware unsupported info")} />
        );
    }

    if (hasDowngradeProtection(data.fileAttributes)) {
        return (
            <InfoBanner
                title={
                    <Trans
                        i18nKey="Dialog##upload firmware downgrade protection info"
                        components={{
                            1: (
                                <TextLink
                                    href="https://wiki.teltonika-gps.com/view/FMB_firmware_errata_04.00.00"
                                    size={TEXT_LINK_SIZES.Small}
                                    target="_blank"
                                >
                                    Teltonika GPS Wiki
                                </TextLink>
                            )
                        }}
                    />
                }
            />
        );
    }

    return null;
};

const sortVehicles = (
    data: any,
    setVehicles: (value: SetStateAction<Vehicles>) => void,
    setMakes: (value: SetStateAction<string[]>) => void
) => {
    const allVehicles: Vehicle[] = data.vehicles;

    const sortedVehicles = allVehicles.reduce<SortedVehicles>(
        (previous, { make, model, vehicle_id, year_start, year_end }) => {
            const currentValue = {
                vehicle_id,
                model: getFirstLetterUppercase(model),
                year_start,
                year_end
            };

            const existingValue = previous[getFirstLetterUppercase(make)] || [];

            return {
                ...previous,
                ...(make
                    ? {
                          [getFirstLetterUppercase(make)]: [
                              ...existingValue,
                              currentValue
                          ]
                      }
                    : {})
            };
        },
        {}
    );

    setVehicles(sortedVehicles);

    setMakes(sortAndReturnArray(Object.keys(sortedVehicles)));
};

const renderPlatforms = (
    platforms: PLATFORMS[],
    t: TFunction<"translation", undefined>
) =>
    platforms
        .map(platform =>
            t("Dialog##platform compatibility##platform", {
                title: t(`General##${platform}`)
            })
        )
        .join(", ");

export {
    isUnsupportedFirmware,
    renderAdditionalBanner,
    renderCanAdapterType,
    renderPlatforms,
    sortVehicles
};
